import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Paper, Grid, Card, CardContent, Button,
  Autocomplete, TextField, Dialog, DialogTitle, DialogContent, DialogActions,
  IconButton, Divider, CircularProgress, Alert, CardActions
} from '@mui/material';
import {
    Assessment as AssessmentIcon, ArrowBack as ArrowBackIcon,
    Delete as DeleteIcon, Edit as EditIcon
} from '@mui/icons-material';

import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_API_URL || 'https://luiatro-health-cloud-production.up.railway.app';

const ReportsPage = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [allPatients, setAllPatients] = useState([]);
    const [selectedPatient, setSelectedPatient] = useState(null);
    const [savedReports, setSavedReports] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [isSelectionOpen, setIsSelectionOpen] = useState(false);

    // 1. CARGA INICIAL
    useEffect(() => {
        const token = localStorage.getItem('token');
        if (!token) { navigate('/login'); return; }
        
        fetch(`${API_URL}/api/patients`, { headers: { 'Authorization': `Bearer ${token}` } })
            .then(res => res.ok ? res.json() : [])
            .then(data => setAllPatients(data))
            .catch(err => console.error(err));
            
        setIsSelectionOpen(true);
    }, [navigate]);

    // 2. CARGAR HISTORIAL
    const loadPatientReports = useCallback(async (patientId) => {
        setIsLoading(true);
        const token = localStorage.getItem('token');
        try {
            // Usamos la ruta optimizada que creamos antes
            const res = await fetch(`${API_URL}/api/reports?patientId=${patientId}`, { headers: { 'Authorization': `Bearer ${token}` } });
            if (res.ok) setSavedReports(await res.json());
        } catch (e) { setError("Error cargando historial"); }
        finally { setIsLoading(false); }
    }, []);

    const handlePatientSelect = (patient) => {
        if (patient) {
            setSelectedPatient(patient);
            loadPatientReports(patient.id);
            setIsSelectionOpen(false);
        }
    };

    // --- NUEVA LÓGICA: GENERAR HISTORIA INTEGRAL ---
    const handleNewHistory = async () => {
        if (!selectedPatient) return;
        
        setIsLoading(true);
        try {
            const token = localStorage.getItem('token');
            // 1. Pedir al servidor que genere el HTML con los datos
            const res = await fetch(`${API_URL}/api/reports/generate-integral/${selectedPatient.id}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            
            if (res.ok) {
                const data = await res.json();
                // 2. Navegar al EditorInforme con el contenido PRE-CARGADO
                navigate(`/editor-informe/${selectedPatient.id}`, { 
                    state: { 
                        initialContent: data.content 
                    } 
                });
            } else {
                console.error("Error servidor:", res.status);
                alert("No se pudo generar la historia. Verifica que el servidor tenga la ruta actualizada.");
            }
        } catch (e) {
            console.error(e);
            alert("Error de conexión.");
        } finally {
            setIsLoading(false);
        }
    };

    // --- ABRIR REPORTE EXISTENTE ---
    const handleOpenReport = async (reportId) => {
        // Necesitamos pedir el contenido completo porque la lista está optimizada
        setIsLoading(true);
        try {
            const token = localStorage.getItem('token');
            // Asumiendo que agregaste la ruta GET /api/reports/:id en el server
            // Si no, esto podría fallar si la lista principal no tiene 'content'
            const res = await fetch(`${API_URL}/api/reports/${reportId}`, { headers: { 'Authorization': `Bearer ${token}` } });
            
            if (res.ok) {
                const report = await res.json();
                navigate(`/editor-informe/${selectedPatient.id}`, { 
                    state: { initialContent: report.content || report.bodyContent } 
                });
            }
        } catch(e) { alert("Error abriendo reporte"); }
        finally { setIsLoading(false); }
    };

    const handleDeleteReport = async (id) => {
        if (!window.confirm("¿Eliminar reporte permanentemente?")) return;
        const token = localStorage.getItem('token');
        try {
            await fetch(`${API_URL}/api/reports/${id}`, { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` } });
            loadPatientReports(selectedPatient.id); 
        } catch (e) { alert("Error al eliminar"); }
    };

    return (
        <Box sx={{ p: 3 }}>
            {/* ENCABEZADO */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <AssessmentIcon color="primary" sx={{ fontSize: 40 }} />
                    <Typography variant="h4" fontWeight="bold">Historias Integrales</Typography>
                </Box>
                <IconButton onClick={() => navigate('/dashboard')}><ArrowBackIcon /></IconButton>
            </Box>

            {/* BARRA DE ACCIONES PACIENTE */}
            {selectedPatient && (
                <Paper sx={{ p: 2, mb: 3, bgcolor: '#e3f2fd', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                    <Box>
                        <Typography variant="h6" fontWeight="bold">{selectedPatient.name} {selectedPatient.lastName}</Typography>
                        <Typography variant="body2">C.I.: {selectedPatient.cedula || selectedPatient.idNumber || 'S/C'}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <Button variant="outlined" onClick={() => setIsSelectionOpen(true)}>Cambiar Paciente</Button>
                        {/* ESTE BOTÓN AHORA ACTIVA LA MAGIA */}
                        <Button variant="contained" size="large" onClick={handleNewHistory} disabled={isLoading}>
                            {isLoading ? 'Generando...' : 'Generar Nueva Historia'}
                        </Button>
                    </Box>
                </Paper>
            )}

            {/* LISTA DE REPORTES */}
            <Grid container spacing={3}>
                <Grid item xs={12}>
                    <Typography variant="h6" gutterBottom>Historial Guardado</Typography>
                    <Divider sx={{ mb: 2 }} />
                    {isLoading && <CircularProgress />}
                    {!isLoading && savedReports.length === 0 && <Typography color="text.secondary">No hay historias guardadas.</Typography>}
                    
                    {savedReports.map(report => (
                        <Card key={report.id} variant="outlined" sx={{ mb: 1 }}>
                            <CardContent sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1.5 }}>
                                <Box>
                                    <Typography color="primary" fontWeight="bold">{report.title}</Typography>
                                    <Typography variant="caption">{new Date(report.createdAt).toLocaleDateString()} - {report.reason}</Typography>
                                </Box>
                                <Box>
                                    <IconButton size="small" color="primary" onClick={() => handleOpenReport(report.id)} sx={{ mr: 1 }}>
                                        <EditIcon />
                                    </IconButton>
                                    <IconButton size="small" color="error" onClick={() => handleDeleteReport(report.id)}>
                                        <DeleteIcon />
                                    </IconButton>
                                </Box>
                            </CardContent>
                        </Card>
                    ))}
                </Grid>
            </Grid>

            {/* DIÁLOGO BUSCADOR */}
            <Dialog open={isSelectionOpen} onClose={() => navigate('/dashboard')} fullWidth maxWidth="sm">
                <DialogTitle>Seleccionar Paciente</DialogTitle>
                <DialogContent dividers>
                    <Autocomplete
                        options={allPatients}
                        getOptionLabel={(p) => `${p.name} ${p.lastName} (${p.cedula || 'S/C'})`}
                        onChange={(_, v) => handlePatientSelect(v)}
                        renderInput={(params) => <TextField {...params} label="Buscar..." autoFocus />}
                    />
                </DialogContent>
                <DialogActions><Button onClick={() => navigate('/dashboard')}>Cancelar</Button></DialogActions>
            </Dialog>
        </Box>
    );
};

export default ReportsPage;